// src/pages/programs/ProgramRegisterPage.jsx
// Public registration page — no auth required. URL: /p/:programId
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  doc, getDoc, addDoc, collection, serverTimestamp,
  query, where, getCountFromServer,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, functions } from '@/firebase/firebase';
import { app } from '@/firebase/firebase';
import { calculateProgramPrice, formatCents } from '@/utils/programPricing';
import { setAppLanguage } from '@/i18n';
import toast from 'react-hot-toast';
import {
  ChevronRightIcon,
  ChevronLeftIcon,
  CheckCircleIcon,
  UserIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';

/* ------------------------------------------------------------------ */
/* Bilingual strings                                                    */
/* ------------------------------------------------------------------ */
const T = {
  en: {
    loading: 'Loading registration…',
    notFoundTitle: 'Program Not Found',
    notFoundBody: 'This registration link may be expired or invalid.',
    notYetOpen: 'Registration is not yet open.',
    opensOn: 'Opens on',
    closedBody: 'Registration for this program is currently closed.',
    draftBody: 'This registration program is not yet open.',
    archivedBody: 'This program has been archived.',
    closedPastTitle: 'Registration for this program has closed.',
    closedOn: 'Closed on',
    registration: 'Registration',
    earlyBird: '🐦 Early bird pricing available — ends',
    regCloses: 'Registration closes',
    stepOf: 'Step',
    of: 'of',
    stepLabels: {
      division: 'Division',
      player: 'Player Info',
      parent: 'Parent / Guardian',
      extra: 'More Info',
      waivers: 'Waivers',
      docs: 'Documents',
      review: 'Review & Submit',
    },
    chooseDivision: 'Choose a Division',
    noDivisions: 'No divisions available.',
    divisionFallback: 'Division',
    full: '🔒 Full — join waitlist',
    spotLeft: 'spot left',
    spotsLeft: 'spots left',
    maxPlayers: 'Max',
    players: 'players',
    earlyBirdLabel: 'Early Bird',
    playerInfo: 'Player Information',
    playerFullName: "Player's Full Name",
    firstLast: 'First Last',
    dateOfBirth: 'Date of Birth',
    gender: 'Gender',
    genderOptions: {
      preferNot: 'Prefer not to say',
      male: 'Male',
      female: 'Female',
      nonbinary: 'Non-binary',
      other: 'Other',
    },
    parentGuardian: 'Parent / Guardian',
    fullName: 'Full Name',
    email: 'Email Address',
    phone: 'Phone Number',
    emailPlaceholder: 'you@example.com',
    phonePlaceholder: '(555) 000-0000',
    welcomeBack: 'Welcome back! We pre-filled your name and phone. You can edit them if anything\'s changed.',
    additionalInfo: 'Additional Information',
    additionalInfoSubtitle: 'Please fill in the following information required for this program.',
    selectDots: 'Select…',
    yes: 'Yes',
    waivers: 'Waivers & Agreements',
    waiversSubtitle: 'Please read and agree to the following before submitting.',
    agreePrefix: 'I have read and agree to this',
    waiverFallback: 'waiver',
    esignTitle: 'Electronic Signature',
    esignDisclosure:
      'By typing your full name and submitting this registration, you agree that your typed name constitutes your legal electronic signature under the U.S. ESIGN Act and UETA, and that you intend to sign each waiver listed above on behalf of your child. A record of your signature, including the date, your device, and the exact waiver text, will be stored for both parties.',
    waiverSign: 'Type your full name to sign',
    waiverSignSub: 'This serves as your electronic signature.',
    waiverSignPlaceholder: 'Your full name',
    waiverSignAppliesTo: (n) => `Your signature above applies to all ${n} waiver${n !== 1 ? 's' : ''} listed above.`,
    errSignatureRequired: 'Please type your full name to sign.',
    requiredDocuments: 'Required Documents',
    uploaded: 'uploaded',
    replace: 'Replace',
    uploading: 'Uploading…',
    tapToUpload: 'Tap to upload or take photo',
    uploadFailed: 'Upload failed — please try again.',
    uploadFailedToast: 'Upload failed — please try again',
    uploadLaterHint: 'You can also upload missing documents after registration via your confirmation email.',
    reviewSubmit: 'Review & Submit',
    summaryDivision: 'Division',
    summaryPlayer: 'Player',
    summaryDob: 'Date of Birth',
    summaryParent: 'Parent / Guardian',
    summaryEmail: 'Parent Email',
    summaryPhone: 'Phone',
    registrationFee: 'Registration Fee',
    subtotal: 'Subtotal',
    processingFee: 'Processing fee',
    processingFeeHint: 'Covers online payment processing (2.9% + $0.30).',
    total: 'Total',
    divisionFullTitle: 'This division is currently full',
    divisionFullBody: "You'll be added to the waitlist. We'll contact you if a spot opens up.",
    freeRegistration: '✓ Free registration — no payment required',
    paymentRedirect: "You'll be redirected to a secure payment page after submitting.",
    submitting: 'Submitting…',
    joinWaitlist: 'Join Waitlist',
    registerPay: 'Register & Pay',
    completeRegistration: 'Complete Registration',
    back: 'Back',
    continue: 'Continue',
    poweredBy: 'Powered by',
    errFillRequired: 'Please fill in all required fields',
    errAgreeWaivers: 'Please agree to all waivers',
    errSubmit: 'Registration failed. Please try again.',
    docLabels: {
      birth_certificate: 'Birth Certificate / Passport',
      player_photo: 'Player Photo',
      club_registration: 'Club Registration Form',
      medical_form: 'Medical / Physical Form',
      insurance_card: 'Insurance Card',
    },
    docHints: {
      birth_certificate: 'Photo of birth certificate or passport — JPG, PNG, or PDF',
      player_photo: 'Clear headshot of the player',
      club_registration: 'US Soccer R002 or similar registration document',
      medical_form: 'Physical exam or medical clearance form',
      insurance_card: 'Front of health insurance card',
      fallback: 'Upload the required document — JPG, PNG, or PDF',
    },
  },
  es: {
    loading: 'Cargando registro…',
    notFoundTitle: 'Programa No Encontrado',
    notFoundBody: 'Este enlace de registro puede haber expirado o ser inválido.',
    notYetOpen: 'El registro aún no está abierto.',
    opensOn: 'Abre el',
    closedBody: 'El registro para este programa está cerrado actualmente.',
    draftBody: 'Este programa de registro aún no está abierto.',
    archivedBody: 'Este programa ha sido archivado.',
    closedPastTitle: 'El registro para este programa ha cerrado.',
    closedOn: 'Cerrado el',
    registration: 'Registro',
    earlyBird: '🐦 Precio de inscripción anticipada disponible — termina',
    regCloses: 'El registro cierra',
    stepOf: 'Paso',
    of: 'de',
    stepLabels: {
      division: 'División',
      player: 'Info del Jugador',
      parent: 'Padre / Tutor',
      extra: 'Más Información',
      waivers: 'Exenciones',
      docs: 'Documentos',
      review: 'Revisar y Enviar',
    },
    chooseDivision: 'Elige una División',
    noDivisions: 'No hay divisiones disponibles.',
    divisionFallback: 'División',
    full: '🔒 Lleno — únete a la lista de espera',
    spotLeft: 'lugar disponible',
    spotsLeft: 'lugares disponibles',
    maxPlayers: 'Máx',
    players: 'jugadores',
    earlyBirdLabel: 'Anticipado',
    playerInfo: 'Información del Jugador',
    playerFullName: 'Nombre Completo del Jugador',
    firstLast: 'Nombre Apellido',
    dateOfBirth: 'Fecha de Nacimiento',
    gender: 'Género',
    genderOptions: {
      preferNot: 'Prefiero no decir',
      male: 'Masculino',
      female: 'Femenino',
      nonbinary: 'No binario',
      other: 'Otro',
    },
    parentGuardian: 'Padre / Tutor',
    fullName: 'Nombre Completo',
    email: 'Correo Electrónico',
    phone: 'Número de Teléfono',
    emailPlaceholder: 'tu@ejemplo.com',
    phonePlaceholder: '(555) 000-0000',
    welcomeBack: '¡Hola de nuevo! Rellenamos tu nombre y teléfono. Puedes editarlos si algo cambió.',
    additionalInfo: 'Información Adicional',
    additionalInfoSubtitle: 'Por favor completa la siguiente información requerida para este programa.',
    selectDots: 'Selecciona…',
    yes: 'Sí',
    waivers: 'Exenciones y Acuerdos',
    waiversSubtitle: 'Por favor lee y acepta lo siguiente antes de enviar.',
    agreePrefix: 'He leído y acepto esta',
    waiverFallback: 'exención',
    esignTitle: 'Firma Electrónica',
    esignDisclosure:
      'Al escribir su nombre completo y enviar este registro, usted acepta que su nombre escrito constituye su firma electrónica legal conforme a las leyes ESIGN y UETA de EE. UU., y que tiene la intención de firmar cada exención en nombre de su hijo/a. Se conservará un registro de su firma, incluyendo la fecha, su dispositivo y el texto exacto de la exención, para ambas partes.',
    waiverSign: 'Escriba su nombre completo para firmar',
    waiverSignSub: 'Esto sirve como su firma electrónica.',
    waiverSignPlaceholder: 'Su nombre completo',
    waiverSignAppliesTo: (n) => `Su firma aplica a todas las ${n} exencion${n !== 1 ? 'es' : ''} listadas arriba.`,
    errSignatureRequired: 'Por favor escriba su nombre completo para firmar.',
    requiredDocuments: 'Documentos Requeridos',
    uploaded: 'subidos',
    replace: 'Reemplazar',
    uploading: 'Subiendo…',
    tapToUpload: 'Toca para subir o tomar foto',
    uploadFailed: 'Error al subir — por favor intenta de nuevo.',
    uploadFailedToast: 'Error al subir — por favor intenta de nuevo',
    uploadLaterHint: 'También puedes subir los documentos faltantes después del registro mediante tu correo de confirmación.',
    reviewSubmit: 'Revisar y Enviar',
    summaryDivision: 'División',
    summaryPlayer: 'Jugador',
    summaryDob: 'Fecha de Nacimiento',
    summaryParent: 'Padre / Tutor',
    summaryEmail: 'Correo del Padre',
    summaryPhone: 'Teléfono',
    registrationFee: 'Costo de Registro',
    subtotal: 'Subtotal',
    processingFee: 'Cargo por procesamiento',
    processingFeeHint: 'Cubre el procesamiento del pago en línea (2.9% + $0.30).',
    total: 'Total',
    divisionFullTitle: 'Esta división está actualmente llena',
    divisionFullBody: 'Serás agregado a la lista de espera. Te contactaremos si se abre un lugar.',
    freeRegistration: '✓ Registro gratuito — no se requiere pago',
    paymentRedirect: 'Serás redirigido a una página de pago segura después de enviar.',
    submitting: 'Enviando…',
    joinWaitlist: 'Unirse a Lista de Espera',
    registerPay: 'Registrar y Pagar',
    completeRegistration: 'Completar Registro',
    back: 'Atrás',
    continue: 'Continuar',
    poweredBy: 'Con tecnología de',
    errFillRequired: 'Por favor completa todos los campos requeridos',
    errAgreeWaivers: 'Por favor acepta todas las exenciones',
    errSubmit: 'Falló el registro. Por favor intenta de nuevo.',
    docLabels: {
      birth_certificate: 'Acta de Nacimiento / Pasaporte',
      player_photo: 'Foto del Jugador',
      club_registration: 'Formulario de Registro del Club',
      medical_form: 'Formulario Médico / Físico',
      insurance_card: 'Tarjeta de Seguro',
    },
    docHints: {
      birth_certificate: 'Foto del acta de nacimiento o pasaporte — JPG, PNG o PDF',
      player_photo: 'Foto de perfil clara del jugador',
      club_registration: 'US Soccer R002 u otro formulario de registro',
      medical_form: 'Formulario de examen físico o autorización médica',
      insurance_card: 'Frente de la tarjeta de seguro médico',
      fallback: 'Sube el documento requerido — JPG, PNG o PDF',
    },
  },
};

// ── helpers ────────────────────────────────────────────────────────────────────
function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

// Design tokens — keep in sync with RegisterPage and the rest of the
// parent-facing surface. ProgramRegisterPage is the SISTER of
// RegisterPage (clubs use programs; teams use seasons). Visual identity
// must match because the same family will hit both flows depending on
// whether they're registering with a club or a single team.
const HEAD_FONT = "'Barlow Condensed', sans-serif";

const inputCls =
  'block w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:border-[#b5f03d] focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/30 transition-colors';

const labelCls = 'block text-sm font-semibold text-[#0d1f14] mb-1.5';

// ── step builder ───────────────────────────────────────────────────────────────
function buildSteps(program) {
  const steps = [
    { key: 'division' },
    { key: 'player' },
    { key: 'parent' },
  ];
  if (program?.extraFields?.length > 0) {
    steps.push({ key: 'extra' });
  }
  if (program?.waivers?.length > 0) {
    steps.push({ key: 'waivers' });
  }
  if (program?.requiredDocTypes?.length > 0) {
    steps.push({ key: 'docs' });
  }
  steps.push({ key: 'review' });
  return steps;
}

// ── main ───────────────────────────────────────────────────────────────────────
export default function ProgramRegisterPage() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = String(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
  const tr = T[lang];

  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Division capacity counts (programId + divisionId)
  const [divisionCounts, setDivisionCounts] = useState({});

  // Form state
  const [selectedDivisionId, setSelectedDivisionId] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [playerDob, setPlayerDob] = useState('');
  const [playerGender, setPlayerGender] = useState('');
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  // Remember-me pre-fill (see RegisterPage.jsx for the same pattern).
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [lastLookedUpEmail, setLastLookedUpEmail] = useState('');

  const handleParentEmailBlur = async (e) => {
    const email = String(e.target.value || '').trim().toLowerCase();
    if (!email || !/\S+@\S+\.\S+/.test(email)) return;
    if (email === lastLookedUpEmail) return;
    setLastLookedUpEmail(email);
    try {
      const fn = httpsCallable(functions, 'lookupParentProfile');
      const { data } = await fn({ email });
      if (!data?.found || !data.profile) return;
      let didPrefill = false;
      if (!parentName.trim()  && data.profile.parentName)  {
        setParentName(data.profile.parentName);
        didPrefill = true;
      }
      if (!parentPhone.trim() && data.profile.parentPhone) {
        setParentPhone(data.profile.parentPhone);
        didPrefill = true;
      }
      if (didPrefill) setShowWelcomeBack(true);
    } catch (err) {
      console.debug('[lookupParentProfile]', err?.message);
    }
  };
  const [waiverSigs, setWaiverSigs] = useState({}); // { waiverId: boolean }
  const [waiverSignedName, setWaiverSignedName] = useState('');
  const [extraFieldValues, setExtraFieldValues] = useState({}); // { fieldId: value }

  // Document uploads — keyed by docType
  const [docUploads, setDocUploads] = useState({});
  // Stable session ID for storage path before registrationId exists
  const [uploadSessionId] = useState(() => crypto.randomUUID());

  // ── load division counts (for maxPlayers / waitlist) ────────────────────────
  useEffect(() => {
    if (!program?.divisions?.length || !programId) return;
    const divisionsWithMax = program.divisions.filter((d) => d.maxPlayers > 0);
    if (!divisionsWithMax.length) return;
    divisionsWithMax.forEach(async (div) => {
      try {
        const snap = await getCountFromServer(
          query(
            collection(db, 'registrations'),
            where('programId', '==', programId),
            where('divisionId', '==', div.id),
          )
        );
        setDivisionCounts((prev) => ({ ...prev, [div.id]: snap.data().count }));
      } catch (e) {
        console.warn('[ProgramRegisterPage] division count error', e);
      }
    });
  }, [program, programId]);

  // ── load program ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!programId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'programs', programId));
        if (!snap.exists()) {
          setProgram(null);
        } else {
          const data = { id: snap.id, ...snap.data() };
          setProgram(data);
          // pre-select first division
          if (data.divisions?.length > 0) {
            setSelectedDivisionId(data.divisions[0].id);
          }
          // init waiver sigs
          const sigs = {};
          (data.waivers ?? []).forEach((w) => { sigs[w.id] = false; });
          setWaiverSigs(sigs);
        }
      } catch (e) {
        console.error('ProgramRegisterPage load error:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [programId]);

  // ── derived pricing + capacity ───────────────────────────────────────────────
  const pricing = program ? calculateProgramPrice(program, selectedDivisionId) : null;
  const selectedDivision = program?.divisions?.find((d) => d.id === selectedDivisionId);
  const isDivisionFull = (divId) => {
    const div = program?.divisions?.find((d) => d.id === divId);
    if (!div?.maxPlayers || div.maxPlayers <= 0) return false;
    return (divisionCounts[divId] ?? 0) >= div.maxPlayers;
  };
  const selectedDivisionFull = selectedDivisionId ? isDivisionFull(selectedDivisionId) : false;

  // ── step derivation (needed before canProceed / nextStep) ───────────────────
  const steps = buildSteps(program);
  const currentKey = steps[step]?.key ?? 'review';
  const isLastStep = step === steps.length - 1;

  // ── validation per step ──────────────────────────────────────────────────────
  const canProceed = () => {
    if (currentKey === 'division') return !!selectedDivisionId;
    if (currentKey === 'player')   return playerName.trim().length > 0;
    if (currentKey === 'parent')   return parentName.trim().length > 0 && parentEmail.trim().length > 0;
    if (currentKey === 'extra') {
      return (program.extraFields ?? []).filter(f => f.required).every(f => {
        const val = extraFieldValues[f.id];
        if (f.type === 'checkbox') return true; // checkboxes don't need to be true to be "answered"
        return val != null && String(val).trim().length > 0;
      });
    }
    if (currentKey === 'waivers') {
      return Object.values(waiverSigs).every(Boolean) && waiverSignedName.trim().length > 0;
    }
    if (currentKey === 'docs') return true; // uploading docs is recommended but not required to proceed
    return true;
  };

  const nextStep = () => {
    if (!canProceed()) {
      // On the waivers step, distinguish between "didn't agree" and "didn't sign".
      if (currentKey === 'waivers') {
        if (!Object.values(waiverSigs).every(Boolean)) {
          toast.error(tr.errAgreeWaivers);
        } else {
          toast.error(tr.errSignatureRequired);
        }
      } else {
        toast.error(tr.errFillRequired);
      }
      return;
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  // ── document upload handler ───────────────────────────────────────────────────
  const handleDocUpload = (docType, file) => {
    if (!file) return;
    const storage = getStorage(app);
    const ext = file.name.split('.').pop();
    const path = `registrations/${uploadSessionId}/docs/${docType}.${ext}`;
    const sRef = storageRef(storage, path);
    const task = uploadBytesResumable(sRef, file);

    setDocUploads(prev => ({ ...prev, [docType]: { status: 'uploading', progress: 0, fileName: file.name } }));

    task.on(
      'state_changed',
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setDocUploads(prev => ({ ...prev, [docType]: { ...prev[docType], progress: pct } }));
      },
      (err) => {
        console.error('[docUpload]', err);
        setDocUploads(prev => ({ ...prev, [docType]: { status: 'error', progress: 0, fileName: file.name } }));
        toast.error(tr.uploadFailedToast);
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setDocUploads(prev => ({ ...prev, [docType]: { status: 'done', progress: 100, url, fileName: file.name } }));
      }
    );
  };

  // ── submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!canProceed()) {
      toast.error(tr.errAgreeWaivers);
      return;
    }

    try {
      setSubmitting(true);

      const regData = {
        // Program identifiers
        programId,
        divisionId: selectedDivisionId,
        clubId: program.clubId,
        teamId: selectedDivision?.teamId ?? null,
        seasonId: null, // explicitly null — program registration

        // Snapshot the parent's preferred language at submit time. Consumed
        // server-side by functions/utils/parentLanguage.resolveParentLanguage
        // so every outbound notification arrives in the parent's preferred
        // language. (Part of features/outbound-bilingual-notifications.)
        language: lang,

        // Player
        playerName: playerName.trim(),
        playerDob: playerDob || null,
        playerGender: playerGender || null,

        // Parent / guardian
        parentName: parentName.trim(),
        parentEmail: parentEmail.trim().toLowerCase(),
        parentPhone: parentPhone.trim() || null,

        // Pricing snapshot
        pricing: {
          finalAmount: pricing?.finalAmount ?? 0,
          baseFee: pricing?.baseFee ?? 0,
          isEarlyBird: pricing?.isEarlyBird ?? false,
          label: pricing?.label ?? 'Standard',
          subtotalAmount:    pricing?.subtotalAmount    ?? (pricing?.finalAmount ?? 0),
          processingFeeMode: pricing?.processingFeeMode ?? 'absorb',
          processingFee:     pricing?.processingFee     ?? 0,
        },
        amountDue: pricing?.finalAmount ?? 0,

        // Waivers — per-waiver checkbox state (legacy field)
        waiverSignatures: Object.entries(waiverSigs).map(([id, signed]) => {
          const w = (program.waivers ?? []).find((x) => x.id === id);
          return { id, name: w?.name ?? '', signed, signedAt: signed ? new Date() : null };
        }),
        // E-signature audit trail — ESIGN/UETA require evidence of WHAT was signed,
        // WHO signed (typed name), WHEN (server timestamp), and HOW (device/IP).
        waiverSignedByName: waiverSignedName.trim() || null,
        waiverSignedAt: serverTimestamp(),
        waiverVersion: program.updatedAt?.seconds?.toString() ?? 'v1',
        waiversSigned: (program.waivers ?? []).map(w => w.name),
        waiverTextSnapshot: (program.waivers ?? []).map(w => ({
          id: w.id || null,
          name: w.name || null,
          text: w.text || null,
        })),
        waiverSignedUserAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
        waiverSignedIp: null, // filled in server-side by sendRegistrationConfirmation
        extraFieldValues: Object.entries(extraFieldValues).map(([id, value]) => {
          const field = (program.extraFields ?? []).find(f => f.id === id);
          return { id, label: field?.label ?? id, value };
        }),

        // Status — waitlist takes priority if division is full
        status: selectedDivisionFull
          ? 'waitlist'
          : pricing?.finalAmount > 0
          ? 'pending_payment'
          : 'confirmed',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        source: 'program_registration',
        documents: Object.fromEntries(
          Object.entries(docUploads)
            .filter(([, d]) => d.status === 'done' && d.url)
            .map(([type, d]) => [type, { url: d.url, fileName: d.fileName, uploadedAt: new Date().toISOString() }])
        ),
        programName: program.name ?? null,
        divisionName: selectedDivision?.name ?? null,
      };

      const ref = await addDoc(collection(db, 'registrations'), regData);

      // For PAID flows the confirmation email is sent by the Stripe
      // webhook (functions/modules/billing.js → checkout.session.completed)
      // AFTER payment actually clears. May 2026 fix — pre-fix, the
      // email was fired here regardless of whether the parent
      // completed payment, so abandoned-at-Stripe parents got
      // misleading "you're confirmed" emails. Now: free / waitlist
      // flows fire the email here; paid flows wait for the webhook.
      const willHitStripe = pricing?.finalAmount > 0 && !selectedDivisionFull;

      // If fee > 0 and NOT waitlisted, redirect to Stripe checkout
      if (willHitStripe) {
        try {
          const createCheckout = httpsCallable(functions, 'createRegistrationCheckout');
          const result = await createCheckout({
            registrationId: ref.id,
            programId,
            teamId: selectedDivision?.teamId ?? null,
            seasonId: null,
            amount: pricing.finalAmount,
            playerName: playerName.trim(),
            parentEmail: parentEmail.trim().toLowerCase(),
            successUrl: `${window.location.origin}/p/${programId}/success?regId=${ref.id}`,
            cancelUrl: window.location.href,
          });
          if (result?.data?.url) {
            window.location.href = result.data.url;
            return;
          }
        } catch (stripeErr) {
          console.warn('Stripe checkout unavailable, registering without payment:', stripeErr);
          // Fall through to success page — payment collected offline.
          // Since Stripe didn't actually take payment, the webhook
          // won't fire either. Send the confirmation email manually
          // so the parent at least gets a receipt of their submission.
          httpsCallable(functions, 'sendRegistrationConfirmation')({ registrationId: ref.id })
            .catch((err) => console.warn('[ProgramRegisterPage] confirmation email skipped', err));
        }
      } else {
        // Free or waitlist flow — no Stripe involvement. Send the
        // confirmation email directly here.
        httpsCallable(functions, 'sendRegistrationConfirmation')({ registrationId: ref.id })
          .catch((err) => console.warn('[ProgramRegisterPage] confirmation email skipped', err));
      }

      navigate(`/p/${programId}/success?regId=${ref.id}`);
    } catch (err) {
      console.error('ProgramRegisterPage submit error:', err);
      toast.error(tr.errSubmit);
    } finally {
      setSubmitting(false);
    }
  };

  // ── loading / not found / closed / not-yet-open ─────────────────────────────
  // All status screens use the same lime/pitch card pattern as the
  // status screens in RegisterPage so a parent who lands on a stale link
  // sees a consistent error surface regardless of whether they came from
  // a season link or a program link.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7faf5]">
        <div className="text-zinc-500 text-sm">{tr.loading}</div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7faf5] px-4">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-8 text-center">
          <h1
            className="text-xl font-bold uppercase tracking-tight text-[#0d1f14] mb-2"
            style={{ fontFamily: HEAD_FONT }}
          >
            {tr.notFoundTitle}
          </h1>
          <p className="text-zinc-600 text-sm">{tr.notFoundBody}</p>
        </div>
      </div>
    );
  }

  // Compute dates before early-return guards so they're available in all branches
  const regClose = toDate(program.registrationClose);
  const regOpen  = toDate(program.registrationOpen);
  const earlyBirdDeadline = toDate(program.earlyBirdDeadline);
  const dateLocale = lang === 'es' ? 'es-ES' : 'en-US';

  // If registrationOpen is in the future, show "not yet open" even if status is 'open'
  if (regOpen && new Date() < regOpen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7faf5] px-4">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-8 text-center">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Registration
          </p>
          <h1
            className="mt-1 text-xl font-bold uppercase tracking-tight text-[#0d1f14] mb-2"
            style={{ fontFamily: HEAD_FONT }}
          >
            {program.name}
          </h1>
          <p className="text-zinc-700 text-sm">{tr.notYetOpen}</p>
          <p className="text-xs text-zinc-500 mt-2">
            {tr.opensOn} {regOpen.toLocaleDateString(dateLocale)}
          </p>
        </div>
      </div>
    );
  }

  if (program.status !== 'open') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7faf5] px-4">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-8 text-center">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Registration
          </p>
          <h1
            className="mt-1 text-xl font-bold uppercase tracking-tight text-[#0d1f14] mb-2"
            style={{ fontFamily: HEAD_FONT }}
          >
            {program.name}
          </h1>
          <p className="text-zinc-700 text-sm">
            {program.status === 'closed'
              ? tr.closedBody
              : program.status === 'draft'
              ? tr.draftBody
              : tr.archivedBody}
          </p>
        </div>
      </div>
    );
  }

  // Even if status is 'open', enforce the registrationClose date
  if (regClose && new Date() > regClose) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7faf5] px-4">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-8 text-center">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Registration
          </p>
          <h1
            className="mt-1 text-xl font-bold uppercase tracking-tight text-[#0d1f14] mb-2"
            style={{ fontFamily: HEAD_FONT }}
          >
            {program.name}
          </h1>
          <p className="text-zinc-700 text-sm">{tr.closedPastTitle}</p>
          <p className="text-xs text-zinc-500 mt-2">
            {tr.closedOn} {regClose.toLocaleDateString(dateLocale)}
          </p>
        </div>
      </div>
    );
  }

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f7faf5] flex flex-col items-center justify-start px-4 py-8">
      <div className="w-full max-w-xl">

        {/* Language toggle — same pill pattern as RegisterPage. */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setAppLanguage(lang === 'en' ? 'es' : 'en')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-zinc-200 text-xs font-bold uppercase text-zinc-600 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d] hover:text-[#5a8a1f] transition-colors"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            <span>{lang === 'en' ? '🇲🇽' : '🇺🇸'}</span>
            {lang === 'en' ? 'Español' : 'English'}
          </button>
        </div>

        {/* Pitch-dark hero — same shape as RegisterPage so a parent who
            registers for a club program sees the same first-touch
            visual identity as one who registers for a single team's
            season. Lime grid overlay + pitch background. */}
        <div
          className="mb-6 relative overflow-hidden rounded-2xl bg-[#0d1f14] px-5 py-6 shadow-[0_2px_6px_0_rgba(13,31,20,0.18)]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(181,240,61,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.06) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        >
          <div className="relative text-center">
            <p
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {tr.registration}
            </p>
            <h1
              className="mt-0.5 text-2xl font-bold uppercase tracking-tight text-white"
              style={{ fontFamily: HEAD_FONT }}
            >
              {program.name}
            </h1>
            {program.sport && (
              <p className="text-sm text-[#b5f03d]/90 mt-1">{program.sport}</p>
            )}

            {/* Info pills — early bird in lime (it's a positive callout),
                deadline in muted white-on-dark for context. */}
            <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
              {earlyBirdDeadline && earlyBirdDeadline > new Date() && (
                <span
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#b5f03d] text-[#0d1f14] text-[11px] font-bold uppercase"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {tr.earlyBird} {earlyBirdDeadline.toLocaleDateString(dateLocale)}
                </span>
              )}
              {regClose && (
                <span
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/10 text-zinc-200 text-[11px] font-medium uppercase tracking-wide"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {tr.regCloses} {regClose.toLocaleDateString(dateLocale)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Progress indicator — pill bars + explicit step counter.
            Parents on mobile filling out a 6-7 step form lose track
            without an at-a-glance "where am I?" cue. Step counter +
            section label sit ABOVE the pills so the next-step
            expectation is the first thing the eye lands on. */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <p
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {tr.stepOf} {step + 1} {tr.of} {steps.length}
              {tr.stepLabels[steps[step]?.key]
                ? `: ${tr.stepLabels[steps[step]?.key]}`
                : ''}
            </p>
            <p className="text-[11px] font-bold text-zinc-500">
              {Math.round(((step + 1) / steps.length) * 100)}%
            </p>
          </div>
          <div className="flex items-center gap-1.5 justify-center">
            {steps.map((s, i) => (
              <div key={s.key} className="flex items-center gap-1.5">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i < step
                      ? 'bg-[#b5f03d] w-6'
                      : i === step
                      ? 'bg-[#b5f03d] w-10 ring-2 ring-[#b5f03d]/30'
                      : 'bg-zinc-200 w-6'
                  }`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] border border-zinc-200 p-6 sm:p-8">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-4"
            style={{ fontFamily: HEAD_FONT }}
          >
            {tr.stepOf} {step + 1} {tr.of} {steps.length} — {tr.stepLabels[steps[step]?.key] ?? ''}
          </p>

          {/* ── STEP: Division selection ────────────────────────────────── */}
          {currentKey === 'division' && (
            <div className="space-y-4">
              <h2
                className="text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.chooseDivision}
              </h2>
              {program.description && (
                <p className="text-sm text-zinc-600 leading-relaxed">{program.description}</p>
              )}

              <div className="space-y-2 mt-2">
                {(program.divisions ?? []).length === 0 && (
                  <p className="text-sm text-zinc-500">{tr.noDivisions}</p>
                )}
                {(program.divisions ?? []).map((div) => {
                  const p = calculateProgramPrice(program, div.id);
                  const selected = selectedDivisionId === div.id;
                  const full = isDivisionFull(div.id);
                  const spotsLeft = div.maxPlayers > 0
                    ? Math.max(0, div.maxPlayers - (divisionCounts[div.id] ?? 0))
                    : null;
                  return (
                    <button
                      key={div.id}
                      type="button"
                      onClick={() => setSelectedDivisionId(div.id)}
                      className={`w-full flex items-center justify-between rounded-xl border-2 p-4 text-left transition-all ${
                        selected
                          ? 'border-[#b5f03d] bg-[#b5f03d]/10'
                          : full
                          ? 'border-amber-200 bg-amber-50/60 hover:border-amber-300'
                          : 'border-zinc-200 hover:border-[#b5f03d]/40 hover:bg-zinc-50'
                      }`}
                    >
                      <div>
                        <p
                          className={`font-bold text-sm uppercase ${selected ? 'text-[#0d1f14]' : 'text-[#0d1f14]'}`}
                          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                        >
                          {div.name || tr.divisionFallback}
                        </p>
                        {div.maxPlayers > 0 && (
                          <p className={`text-xs mt-0.5 ${full ? 'text-amber-700 font-semibold' : 'text-zinc-500'}`}>
                            {full
                              ? tr.full
                              : spotsLeft !== null
                              ? `${spotsLeft} ${spotsLeft === 1 ? tr.spotLeft : tr.spotsLeft}`
                              : `${tr.maxPlayers} ${div.maxPlayers} ${tr.players}`}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p
                          className={`text-base font-bold ${selected ? 'text-[#0d1f14]' : 'text-[#0d1f14]'}`}
                          style={{ fontFamily: HEAD_FONT }}
                        >
                          {formatCents(p.finalAmount)}
                        </p>
                        {p.isEarlyBird && (
                          <p className="text-[11px] text-emerald-700 font-bold uppercase tracking-wide"
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                          >
                            {tr.earlyBirdLabel}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── STEP: Player info ───────────────────────────────────────── */}
          {currentKey === 'player' && (
            <div className="space-y-4">
              <h2
                className="text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.playerInfo}
              </h2>

              <div>
                <label className={labelCls}>{tr.playerFullName} <span className="text-red-500">*</span></label>
                <input
                  className={inputCls}
                  placeholder={tr.firstLast}
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{tr.dateOfBirth}</label>
                  <input
                    type="date"
                    className={inputCls}
                    value={playerDob}
                    onChange={(e) => setPlayerDob(e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls}>{tr.gender}</label>
                  <select
                    className={inputCls}
                    value={playerGender}
                    onChange={(e) => setPlayerGender(e.target.value)}
                  >
                    <option value="">{tr.genderOptions.preferNot}</option>
                    <option value="male">{tr.genderOptions.male}</option>
                    <option value="female">{tr.genderOptions.female}</option>
                    <option value="nonbinary">{tr.genderOptions.nonbinary}</option>
                    <option value="other">{tr.genderOptions.other}</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP: Parent / guardian ─────────────────────────────────── */}
          {currentKey === 'parent' && (
            <div className="space-y-4">
              <h2
                className="text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.parentGuardian}
              </h2>

              {showWelcomeBack && (
                <div className="rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-4 py-3 text-sm text-[#3d6010]">
                  {tr.welcomeBack}
                </div>
              )}

              {/* Message-language choice — mirrors RegisterPage: the
                  registration.language snapshot routes every future
                  message, so it must be an explicit, bilingual, in-form
                  choice, not just the top-corner pill. Selecting flips
                  the page locale (the same state the submit reads). */}
              <div className="rounded-xl border border-[#b5f03d]/50 bg-[#b5f03d]/10 p-3.5">
                <p className="text-sm font-semibold text-[#0d1f14] leading-snug">
                  What language should team messages arrive in?
                </p>
                <p className="text-sm font-semibold text-[#0d1f14] leading-snug mb-2.5">
                  ¿En qué idioma quieres recibir los mensajes del equipo?
                </p>
                <div className="flex flex-wrap gap-2">
                  {[['en', 'English'], ['es', 'Español']].map(([code, label]) => (
                    <button key={code} type="button"
                      onClick={() => setAppLanguage(code)}
                      className={
                        'px-4 py-2 rounded-full text-sm font-bold uppercase border-2 transition-all ' +
                        (lang === code
                          ? 'border-[#b5f03d] bg-[#b5f03d]/20 text-[#0d1f14]'
                          : 'border-zinc-200 bg-white text-zinc-600 hover:border-[#b5f03d]/40 hover:text-[#0d1f14]')
                      }
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>{tr.fullName} <span className="text-red-500">*</span></label>
                <input
                  className={inputCls}
                  placeholder={tr.firstLast}
                  value={parentName}
                  onChange={(e) => {
                    setParentName(e.target.value);
                    if (showWelcomeBack) setShowWelcomeBack(false);
                  }}
                />
              </div>

              <div>
                <label className={labelCls}>{tr.email} <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  className={inputCls}
                  placeholder={tr.emailPlaceholder}
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  onBlur={handleParentEmailBlur}
                />
              </div>

              <div>
                <label className={labelCls}>{tr.phone}</label>
                <input
                  type="tel"
                  className={inputCls}
                  placeholder={tr.phonePlaceholder}
                  value={parentPhone}
                  onChange={(e) => {
                    setParentPhone(e.target.value);
                    if (showWelcomeBack) setShowWelcomeBack(false);
                  }}
                />
              </div>
            </div>
          )}

          {/* ── STEP: Extra Fields ──────────────────────────────────────── */}
          {currentKey === 'extra' && (
            <div className="space-y-4">
              <h2
                className="text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.additionalInfo}
              </h2>
              <p className="text-sm text-zinc-600">
                {tr.additionalInfoSubtitle}
              </p>
              {(program.extraFields ?? []).map((f) => (
                <div key={f.id}>
                  <label className={labelCls}>
                    {f.label}
                    {f.required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  {f.type === 'text' && (
                    <input
                      className={inputCls}
                      value={extraFieldValues[f.id] ?? ''}
                      onChange={(e) => setExtraFieldValues(prev => ({ ...prev, [f.id]: e.target.value }))}
                    />
                  )}
                  {f.type === 'textarea' && (
                    <textarea
                      className={`${inputCls} min-h-[80px] resize-y`}
                      rows={3}
                      value={extraFieldValues[f.id] ?? ''}
                      onChange={(e) => setExtraFieldValues(prev => ({ ...prev, [f.id]: e.target.value }))}
                    />
                  )}
                  {f.type === 'select' && (
                    <select
                      className={inputCls}
                      value={extraFieldValues[f.id] ?? ''}
                      onChange={(e) => setExtraFieldValues(prev => ({ ...prev, [f.id]: e.target.value }))}
                    >
                      <option value="">{tr.selectDots}</option>
                      {(f.options ?? []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  )}
                  {f.type === 'checkbox' && (
                    <label className="flex items-center gap-3 cursor-pointer mt-1">
                      <input
                        type="checkbox"
                        checked={!!extraFieldValues[f.id]}
                        onChange={(e) => setExtraFieldValues(prev => ({ ...prev, [f.id]: e.target.checked }))}
                        className="h-4 w-4 rounded border-zinc-300 text-[#5a8a1f] focus:ring-[#b5f03d]"
                      />
                      <span className="text-sm text-[#0d1f14]">{tr.yes}</span>
                    </label>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── STEP: Waivers (if any) ──────────────────────────────────── */}
          {currentKey === 'waivers' && (
            <div className="space-y-4">
              <h2
                className="text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.waivers}
              </h2>
              <p className="text-sm text-zinc-600">
                {tr.waiversSubtitle}
              </p>

              {program.waivers.map((w) => (
                <div key={w.id} className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 space-y-3">
                  <p className="text-sm font-semibold text-[#0d1f14]">{w.name}</p>
                  <div className="max-h-40 overflow-y-auto rounded-lg bg-white border border-zinc-200 p-3">
                    <p className="text-xs text-zinc-700 leading-relaxed whitespace-pre-wrap">{w.text}</p>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!waiverSigs[w.id]}
                      onChange={(e) =>
                        setWaiverSigs((prev) => ({ ...prev, [w.id]: e.target.checked }))
                      }
                      className="h-4 w-4 rounded border-zinc-300 text-[#5a8a1f] focus:ring-[#b5f03d]"
                    />
                    <span className="text-sm text-[#0d1f14]">
                      {tr.agreePrefix} {w.name || tr.waiverFallback}
                    </span>
                  </label>
                </div>
              ))}

              {/* Single electronic signature for all waivers — ESIGN/UETA compliant */}
              <div className="rounded-xl border border-zinc-200 bg-white p-4 space-y-3 mt-4">
                <p
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  ✍️ {tr.esignTitle}
                </p>
                <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                  <p className="text-[11px] text-zinc-600 leading-relaxed">{tr.esignDisclosure}</p>
                </div>
                <div>
                  <label className={labelCls}>
                    {tr.waiverSign} <span className="text-red-500">*</span>
                  </label>
                  <input
                    className={`${inputCls} max-w-sm font-medium`}
                    placeholder={tr.waiverSignPlaceholder}
                    value={waiverSignedName}
                    onChange={(e) => setWaiverSignedName(e.target.value)}
                  />
                  <p className="text-xs text-zinc-500 mt-1">{tr.waiverSignSub}</p>
                </div>
                <p className="text-xs text-zinc-500">
                  {tr.waiverSignAppliesTo(program.waivers?.length || 0)}
                </p>
              </div>
            </div>
          )}

          {/* ── STEP: Documents ─────────────────────────────────────── */}
          {currentKey === 'docs' && (() => {
            const DOC_CONFIG = {
              birth_certificate: { accept: 'image/*,application/pdf', icon: '🪪' },
              player_photo:      { accept: 'image/*',                 icon: '📸' },
              club_registration: { accept: 'image/*,application/pdf', icon: '📋' },
              medical_form:      { accept: 'image/*,application/pdf', icon: '🩺' },
              insurance_card:    { accept: 'image/*,application/pdf', icon: '🏥' },
            };
            const required = program.requiredDocTypes ?? [];
            const uploadedCount = required.filter(t => docUploads[t]?.status === 'done').length;
            return (
              <div className="space-y-4">
                <h2
                  className="text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {tr.requiredDocuments}
                </h2>

                {/* Progress bar — lime fill matches the "you're making
                    progress" feel of the rest of the page. Emerald was
                    fine, but visually pulling it toward lime keeps the
                    palette tight. */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#b5f03d] rounded-full transition-all duration-500"
                      style={{ width: required.length > 0 ? `${(uploadedCount / required.length) * 100}%` : '0%' }}
                    />
                  </div>
                  <span
                    className="text-xs font-bold uppercase tracking-wide text-[#5a8a1f] shrink-0"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    {uploadedCount}/{required.length} {tr.uploaded}
                  </span>
                </div>

                {required.map((docType) => {
                  const baseCfg = DOC_CONFIG[docType] ?? {
                    accept: 'image/*,application/pdf',
                    icon: '📄',
                  };
                  const cfg = {
                    ...baseCfg,
                    label: tr.docLabels[docType] ?? docType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                    hint: tr.docHints[docType] ?? tr.docHints.fallback,
                  };
                  const upload = docUploads[docType] ?? { status: 'idle' };
                  const isDone      = upload.status === 'done';
                  const isUploading = upload.status === 'uploading';
                  const isError     = upload.status === 'error';

                  return (
                    <div
                      key={docType}
                      className={`rounded-xl border-2 bg-white overflow-hidden transition-all ${
                        isDone ? 'border-[#b5f03d]' : isError ? 'border-red-300' : 'border-zinc-200'
                      }`}
                    >
                      {/* Header */}
                      <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-100">
                        <span className="text-xl">{cfg.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-[#0d1f14]">{cfg.label}</p>
                          <p className="text-xs text-zinc-500 mt-0.5">{cfg.hint}</p>
                        </div>
                        {isDone && <CheckCircleIcon className="h-5 w-5 text-[#5a8a1f] shrink-0" />}
                      </div>

                      {/* Body */}
                      <div className="px-4 py-3">
                        {isDone ? (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-[#5a8a1f] font-semibold flex items-center gap-2">
                              <CheckCircleIcon className="h-4 w-4" />
                              {upload.fileName}
                            </span>
                            <label className="text-xs text-zinc-500 hover:text-[#5a8a1f] underline cursor-pointer">
                              {tr.replace}
                              <input
                                type="file"
                                accept={cfg.accept}
                                className="hidden"
                                onChange={e => e.target.files?.[0] && handleDocUpload(docType, e.target.files[0])}
                              />
                            </label>
                          </div>
                        ) : isUploading ? (
                          <div className="space-y-1.5">
                            <div className="flex justify-between text-xs text-zinc-500">
                              <span>{tr.uploading}</span>
                              <span>{upload.progress ?? 0}%</span>
                            </div>
                            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#b5f03d] rounded-full transition-all"
                                style={{ width: `${upload.progress ?? 0}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <label className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-zinc-300 text-zinc-600 hover:border-[#b5f03d] hover:text-[#5a8a1f] hover:bg-[#b5f03d]/5 transition-all text-sm font-semibold uppercase cursor-pointer"
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                          >
                            <ArrowUpTrayIcon className="h-5 w-5" />
                            {tr.tapToUpload}
                            <input
                              type="file"
                              accept={cfg.accept}
                              className="hidden"
                              onChange={e => e.target.files?.[0] && handleDocUpload(docType, e.target.files[0])}
                            />
                          </label>
                        )}
                        {isError && (
                          <p className="text-xs text-red-500 mt-1">{tr.uploadFailed}</p>
                        )}
                      </div>
                    </div>
                  );
                })}

                <p className="text-xs text-zinc-500 text-center pt-1">
                  {tr.uploadLaterHint}
                </p>
              </div>
            );
          })()}

          {/* ── STEP: Review & Submit ───────────────────────────────────── */}
          {currentKey === 'review' && (
            <div className="space-y-5">
              <h2
                className="text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.reviewSubmit}
              </h2>

              {/* Summary card */}
              <div className="rounded-xl bg-zinc-50 border border-zinc-200 divide-y divide-zinc-200">
                <SummaryRow label={tr.summaryDivision} value={selectedDivision?.name || '—'} />
                <SummaryRow label={tr.summaryPlayer} value={playerName} />
                {playerDob && <SummaryRow label={tr.summaryDob} value={playerDob} />}
                <SummaryRow label={tr.summaryParent} value={parentName} />
                <SummaryRow label={tr.summaryEmail} value={parentEmail} />
                {parentPhone && <SummaryRow label={tr.summaryPhone} value={parentPhone} />}
              </div>

              {/* Fee — pitch-dark with lime accents, mirrors the
                  "moment of truth" final amount banner in RegisterPage. */}
              {pricing && (
                <div
                  className="relative overflow-hidden rounded-2xl bg-[#0d1f14] p-5 text-white shadow-[0_2px_6px_0_rgba(13,31,20,0.18)]"
                  style={{
                    backgroundImage:
                      'linear-gradient(rgba(181,240,61,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.06) 1px, transparent 1px)',
                    backgroundSize: '32px 32px',
                  }}
                >
                  <div className="relative flex items-center justify-between gap-3">
                    <div>
                      <p
                        className="font-bold uppercase tracking-[1.4px] text-[#b5f03d] text-[11px]"
                        style={{ fontFamily: HEAD_FONT }}
                      >
                        {tr.registrationFee}
                      </p>
                      <p className="text-xs text-[#b5f03d]/90 mt-0.5">{pricing.label}</p>
                    </div>
                    <p
                      className="text-3xl font-bold"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      {formatCents(pricing.finalAmount)}
                    </p>
                  </div>
                  {pricing.processingFee > 0 && (
                    <div className="relative mt-3 pt-3 border-t border-[#b5f03d]/20 space-y-1 text-xs text-zinc-300">
                      <div className="flex justify-between">
                        <span>{tr.subtotal}</span>
                        <span>{formatCents(pricing.subtotalAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span title={tr.processingFeeHint}>{tr.processingFee}</span>
                        <span>+{formatCents(pricing.processingFee)}</span>
                      </div>
                      <div
                        className="flex justify-between font-bold text-white pt-1 border-t border-[#b5f03d]/20"
                        style={{ fontFamily: HEAD_FONT }}
                      >
                        <span>{tr.total}</span>
                        <span>{formatCents(pricing.finalAmount)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedDivisionFull && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
                  <p className="text-sm font-semibold text-amber-800">{tr.divisionFullTitle}</p>
                  <p className="text-xs text-amber-700 mt-1">
                    {tr.divisionFullBody}
                  </p>
                </div>
              )}
              {!selectedDivisionFull && pricing?.finalAmount === 0 && (
                <p className="text-sm text-emerald-700 text-center font-bold uppercase tracking-wide"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {tr.freeRegistration}
                </p>
              )}
              {!selectedDivisionFull && pricing?.finalAmount > 0 && (
                <p className="text-xs text-zinc-500 text-center">
                  {tr.paymentRedirect}
                </p>
              )}

              <button
                type="button"
                disabled={submitting}
                onClick={handleSubmit}
                className={`w-full rounded-xl py-3.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] active:scale-[0.97] disabled:opacity-50 transition-all ${
                  selectedDivisionFull
                    ? 'bg-amber-600 hover:bg-amber-700 text-white'
                    : 'bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14]'
                }`}
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {submitting
                  ? tr.submitting
                  : selectedDivisionFull
                  ? tr.joinWaitlist
                  : pricing?.finalAmount > 0
                  ? `${tr.registerPay} ${formatCents(pricing.finalAmount)}`
                  : tr.completeRegistration}
              </button>
            </div>
          )}

          {/* ── Navigation ───────────────────────────────────────────────── */}
          {!isLastStep && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-zinc-100">
              <button
                type="button"
                onClick={prevStep}
                disabled={step === 0}
                className="inline-flex items-center gap-1.5 text-sm font-bold uppercase text-zinc-500 hover:text-[#0d1f14] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <ChevronLeftIcon className="h-4 w-4" />
                {tr.back}
              </button>

              <button
                type="button"
                onClick={nextStep}
                className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] active:scale-[0.97] transition-all"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {tr.continue}
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          )}

          {isLastStep && step > 0 && (
            <div className="mt-4">
              <button
                type="button"
                onClick={prevStep}
                className="inline-flex items-center gap-1.5 text-sm font-bold uppercase text-zinc-500 hover:text-[#0d1f14] transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <ChevronLeftIcon className="h-4 w-4" />
                {tr.back}
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-zinc-500 mt-6">
          {tr.poweredBy}{' '}
          <span className="font-bold uppercase text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>Scrimly</span>
        </p>
      </div>
    </div>
  );
}

// ── local helpers ──────────────────────────────────────────────────────────────
function SummaryRow({ label, value }) {
  // Tiny hard-coded HEAD_FONT mirror so the helper stays self-contained
  // (it's exported below the main component which can't access the
  // module-scope constant without an explicit reference).
  const HEAD = "'Barlow Condensed', sans-serif";
  return (
    <div className="flex items-center justify-between px-4 py-3 gap-4">
      <span
        className="text-[10px] font-bold uppercase tracking-[1.2px] text-zinc-500 shrink-0"
        style={{ fontFamily: HEAD }}
      >
        {label}
      </span>
      <span className="text-sm font-semibold text-[#0d1f14] text-right truncate">{value}</span>
    </div>
  );
}
